"use client"

import { type FormEvent, useEffect, useLayoutEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { EntityDetailSkeleton } from "@/components/layout/entity-detail-skeleton"
import {
  OrganisationIdentityFields,
  type OrganisationIdentityFieldsValue,
} from "@/components/account/organisation-identity-fields"
import { SettingsFormFooter } from "@/components/settings/settings-form-footer"
import { SettingsPageShell } from "@/components/settings/settings-page-shell"
import { SettingsPanel } from "@/components/settings/settings-panel"
import {
  useManagedOrganisationDetail,
  useOrganisations,
} from "@/hooks/use-organisations"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import {
  employerOrganisationHomePath,
  managedEmployerOrgSlugStorageKey,
} from "@/constants"
import {
  EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT,
  employerOrganisationSettingsPath,
  getEmployerOrganisationSettingsSection,
} from "@/constants/employer-organisation-settings-nav"
import { navigateWithThreadPreservation } from "@/utils/thread-navigation"
import { invalidateEmployerOrganisationScopedQueries } from "@/utils/employer-query-invalidation"
import { useQueryClient } from "@tanstack/react-query"

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function normalizeSlugInput(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "-")
}

type EmployerOrganisationSettingsPageProps = {
  /** When set (hub gate), skips URL params. */
  slug?: string
}

export function EmployerOrganisationSettingsPage({ slug: slugProp }: EmployerOrganisationSettingsPageProps) {
  const { slug: slugParam } = useParams<{ slug: string }>()
  const routeSlug = slugProp?.trim() || slugParam?.trim() || ""
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const ctx = useEmployerOrganisationOptional()
  const homePath = employerOrganisationHomePath()

  const generalSection = getEmployerOrganisationSettingsSection(
    EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT,
  )

  const { data: organisation, isLoading, error } = useManagedOrganisationDetail(routeSlug || undefined)
  const { updateOrganisation, deleteOrganisation, isUpdating, isDeleting, uploadOrganisationLogo, deleteOrganisationLogo, isUploadingOrganisationLogo, isDeletingOrganisationLogo } = useOrganisations()

  const [draft, setDraft] = useState<OrganisationIdentityFieldsValue>({ name: "", slug: "" })

  useLayoutEffect(() => {
    if (!routeSlug || !ctx?.enabled) return
    try {
      window.localStorage.setItem(managedEmployerOrgSlugStorageKey(), routeSlug)
    } catch {
      /* ignore */
    }
    ctx.setActiveOrganisationSlug(routeSlug)
  }, [routeSlug, ctx])

  useEffect(() => {
    if (!organisation) return
    setDraft({ name: organisation.name, slug: organisation.slug })
  }, [organisation])

  const savedName = (organisation?.name ?? "").trim()
  const savedSlug = (organisation?.slug ?? "").trim()
  const normalizedSlug = normalizeSlugInput(draft.slug)
  const isNameDirty = draft.name.trim() !== savedName
  const isSlugDirty = normalizedSlug !== savedSlug
  const isDirty = isNameDirty || isSlugDirty

  const slugValidationError =
    isSlugDirty && normalizedSlug && !SLUG_PATTERN.test(normalizedSlug)
      ? "Use lowercase letters, numbers, and hyphens only."
      : isSlugDirty && !normalizedSlug
        ? "Slug is required."
        : null

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!organisation || !routeSlug || !isDirty || slugValidationError) return

    const payload: { name?: string; slug?: string } = {}
    if (isNameDirty) payload.name = draft.name.trim()
    if (isSlugDirty) payload.slug = normalizedSlug

    updateOrganisation(
      { slug: routeSlug, data: payload },
      {
        onSuccess: (updated) => {
          if (updated.slug !== routeSlug) {
            queryClient.removeQueries({ queryKey: ["organisation", routeSlug] })
            queryClient.setQueryData(["organisation", updated.slug], updated)
            try {
              window.localStorage.setItem(managedEmployerOrgSlugStorageKey(), updated.slug)
            } catch {
              /* ignore */
            }
            ctx?.setActiveOrganisationSlug(updated.slug)
            invalidateEmployerOrganisationScopedQueries(queryClient)
            navigateWithThreadPreservation(
              navigate,
              employerOrganisationSettingsPath(
                updated.slug,
                EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT,
              ),
              searchParams,
            )
          } else {
            queryClient.setQueryData(["organisation", routeSlug], updated)
          }
          toast.success("Organisation updated")
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : "Failed to update organisation"
          toast.error(message)
        },
      },
    )
  }

  const handleCancelOrganisation = () => {
    if (!organisation || !routeSlug) return
    if (
      !window.confirm(
        `Cancel "${organisation.name}"? This permanently removes the organisation — this cannot be undone.`,
      )
    ) {
      return
    }

    deleteOrganisation(routeSlug, {
      onSuccess: () => {
        queryClient.removeQueries({ queryKey: ["organisation", routeSlug] })
        try {
          window.localStorage.removeItem(managedEmployerOrgSlugStorageKey())
        } catch {
          /* ignore */
        }
        invalidateEmployerOrganisationScopedQueries(queryClient)
        navigateWithThreadPreservation(navigate, homePath, searchParams)
        toast.success("Organisation cancelled")
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : "Failed to cancel organisation"
        toast.error(message)
      },
    })
  }

  if (!routeSlug) {
    return (
      <SettingsPageShell title={generalSection?.label ?? "General"}>
        <div className="text-center text-sm text-muted-foreground">Missing organisation.</div>
      </SettingsPageShell>
    )
  }

  if (isLoading && !organisation) {
    return (
      <SettingsPageShell
        title={generalSection?.label ?? "General"}
        description={generalSection?.description}
      >
        <EntityDetailSkeleton variant="settings" withPageContainer={false} ariaLabel="Loading organisation settings" />
      </SettingsPageShell>
    )
  }

  if (error || !organisation) {
    return (
      <SettingsPageShell title="Organisation not found">
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            This organisation does not exist or you do not have access to it.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigateWithThreadPreservation(navigate, homePath, searchParams)}
          >
            Back to organisation
          </Button>
        </div>
      </SettingsPageShell>
    )
  }

  return (
    <SettingsPageShell
      title={generalSection?.label ?? "General"}
      description={generalSection?.description}
    >
      <SettingsPanel as="form" onSubmit={handleSubmit}>
        <OrganisationIdentityFields
          layout="settings"
          value={draft}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
          logoUrl={organisation.logo_url}
          logoFieldKey={`${organisation.logo_url ?? "no-logo"}:${organisation.updated_at}`}
          uploadLogo={(file) => uploadOrganisationLogo({ slug: routeSlug, file })}
          deleteLogo={
            organisation.logo_url ? () => deleteOrganisationLogo(routeSlug) : undefined
          }
          isUploadingLogo={isUploadingOrganisationLogo}
          isDeletingLogo={isDeletingOrganisationLogo}
          disabled={isUpdating}
        />

        {slugValidationError ? (
          <p className="px-4 pb-2 text-xs text-destructive">{slugValidationError}</p>
        ) : null}

        <SettingsFormFooter>
          <Button
            type="submit"
            size="sm"
            disabled={isUpdating || !isDirty || Boolean(slugValidationError)}
          >
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                Saving...
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </SettingsFormFooter>
      </SettingsPanel>

      <Card className="border-destructive/40 bg-destructive/[0.03] dark:bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-destructive">Cancel organisation</CardTitle>
            <CardDescription alwaysShow>
              Permanently remove this organisation and its workspace. This cannot be undone and may affect
              postings that reference it.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-end">
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting}
              onClick={handleCancelOrganisation}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Cancelling...
                </>
              ) : (
                "Cancel organisation"
              )}
            </Button>
          </CardContent>
        </Card>
    </SettingsPageShell>
  )
}
